import * as React from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { getAuth, signOut } from 'firebase/auth';
import { app, db } from '../../firebase/firebaseConfig';
import {
  HCard,
  HSecondaryButton,
} from '../h';
import { BillingSection } from './BillingSection';
import { useMock } from '../Shell/mockContext';

/* AccountTab — FEAT-133 v9 §6.5.

   Tab content only — no outer shell (AppShell provides the chrome + tab strip).
   Sections:
     1. Identity card (avatar initials + name + email, no edit button).
     2. Sign out button (full-width HSecondaryButton, muted red text).

   Props: { accountUUID, userEmail }
   Data:  uses the useMock() context when previewed; in production subscribes
          to accountsInfo/{accountUUID} via onSnapshot.
*/

function getInitials(name) {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function AccountTab({ accountUUID, userEmail }) {
  const mock = useMock();
  const [info, setInfo] = React.useState(() => mock?.accountInfo || null);
  const [signingOut, setSigningOut] = React.useState(false);

  React.useEffect(() => {
    if (mock?.accountInfo) {
      setInfo(mock.accountInfo);
      return undefined;
    }
    if (!accountUUID) return undefined;
    const ref = doc(db, 'accountsInfo', accountUUID);
    const detach = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setInfo({});
          return;
        }
        setInfo(snap.data());
      },
      (err) => {
        console.warn('AccountTab listener error', err.code);
      },
    );
    return () => detach();
  }, [mock, accountUUID]);

  const displayName = info?.name || '';
  const displayEmail = info?.email || userEmail || '';

  const handleSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await signOut(getAuth(app));
      // Clear the persisted app state by navigating to / — the root route
      // renders the sign-in page when unauthenticated. A hard `window.location`
      // reset is the simplest way to ensure no stale post-auth component
      // (e.g. useAccountInfo Firestore listener) keeps running with invalid
      // auth and to avoid a brief flicker of blank /app/* state.
      window.location.href = '/';
    } catch (err) {
      console.warn('Sign out failed', err);
      setSigningOut(false);
    }
  };

  const initials = getInitials(displayName);

  return (
    <div className="px-4 pt-3 pb-6">
      {/* Identity card */}
      <HCard className="p-3 mb-4 flex items-center gap-3">
        <div
          className="flex items-center justify-center rounded-full flex-shrink-0 bg-slate-900 text-white font-semibold"
          style={{ width: 44, height: 44, fontSize: 16 }}
          aria-hidden="true"
        >
          {initials || '—'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-slate-900 truncate leading-tight">
            {displayName || '—'}
          </div>
          <div className="text-[12px] text-slate-500 truncate leading-tight mt-0.5">
            {displayEmail || '—'}
          </div>
        </div>
      </HCard>

      {/* Subscription / billing — only when the admin has enabled billing
          info for this account (flag absent ⇒ hidden). */}
      {info?.billingInfoVisible === true && (
        <BillingSection accountUUID={accountUUID} />
      )}

      {/* Sign out — grey/muted, not destructive. */}
      <HSecondaryButton
        type="button"
        onClick={handleSignOut}
        disabled={signingOut}
        className="w-full text-[13px] text-slate-500 hover:text-slate-700"
      >
        {signingOut ? 'Signing out…' : 'Sign out'}
      </HSecondaryButton>
    </div>
  );
}

export default AccountTab;
